import { alpha } from "@mui/material";
import { ALPHA } from "../../../../theme/tokens";
import { categoricalColorAt } from "../../../../utils/categoricalColors";
import type { HrisIntegrationType } from "../../../../utils/models";
import { EmrType, ExternalSource } from "../../../../utils/models";

/**
 * Whether the department's timezone picker is frozen.
 *
 * DEFAULT: an unknown answer (`hasShifts === undefined`) means LOCKED. The two failure modes are
 * not symmetric — wrongly unlocking re-labels every timestamp the department has already scheduled
 * or punched, while wrongly locking costs a page reload. `undefined` shows up whenever the probe is
 * disabled, in flight, or failed, and none of those is evidence of "no shifts".
 *
 * `!isNewDept` must remain PART OF the expression. `isNewDept` is local state in `useDeptConfig`
 * and `chosenDepartment` still points at the previously selected real department while it is true,
 * so the probe is disabled and its answer is either `undefined` or a stale `true` from that other
 * department. Drop the guard and rule 1 permanently locks a brand-new department's timezone —
 * though it is a required explicit choice there (`context/product/team.md`).
 *
 * (Operand ORDER is not load-bearing — `&&` is commutative for booleans, and the spec confirms a
 * swap changes nothing. The two things that ARE load-bearing are the `?? true` direction and the
 * presence of the `isNewDept` guard; `deriveTimezoneLocked.spec.ts` fails if either is altered.)
 *
 * The code this replaced defaulted the OTHER way (`chosenDepartment.has_shifts ?? false`), which was
 * only safe while the flag arrived pre-computed with /user_info. Do not "restore" it.
 */
export const deriveTimezoneLocked = (isNewDept: boolean, hasShifts: boolean | undefined) => !isNewDept && (hasShifts ?? true);

export const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/;
/** OID format: numeric segments separated by dots, e.g. 2.16.124.113611.6432.1.1.38443 */
export const FACILITY_OID_REGEX = /^\d+(\.\d+)+$/;

/** PostgreSQL INTEGER is int32 — values above this will be rejected by the DB. */
export const MAX_PCC_FACILITY_ID = 2_147_483_647;

/** One HRIS vendor as the integration surfaces present it: what it is called, and how it is drawn. */
export type HrisSourceOption = {
	value: ExternalSource;
	label: string;
	/**
	 * Letterform for the vendor's `BrandMarkTile` — REQUIRED, so a source can never be added to this
	 * catalog without a decision about how it is drawn. Hand-written rather than derived because the
	 * three Pay* vendors collide under every mechanical rule ("Paycom" / "Paycom SFTP" / "Paycor" all
	 * start "Pa"): `Paycom SFTP` is marked by the thing that distinguishes it in this product — that
	 * it is the SFTP feed — instead of by a third letter nobody can tell apart at 40px.
	 */
	mark: string;
};

/**
 * The HRIS vendors a department can integrate with — a curated SUBSET of `ExternalSource`.
 * `clipboard` is deliberately absent: it is agency staffing, not an HRIS, and a card for it here
 * would render with a blank source.
 */
export const HRIS_SOURCES: HrisSourceOption[] = [
	{ value: ExternalSource.Adp, label: "ADP", mark: "ADP" },
	{ value: ExternalSource.Aod, label: "Attendance on Demand", mark: "AoD" },
	{ value: ExternalSource.Chs, label: "CHS Payroll", mark: "CHS" },
	{ value: ExternalSource.Empeon, label: "Empeon", mark: "Em" },
	{ value: ExternalSource.Excelforce, label: "ExcelForce", mark: "EF" },
	{ value: ExternalSource.Flcl, label: "FLCL", mark: "FL" },
	{ value: ExternalSource.Hostdime, label: "Hostdime", mark: "HD" },
	{ value: ExternalSource.Isolved, label: "iSolved", mark: "iS" },
	{ value: ExternalSource.MatrixHr, label: "Matrix HR", mark: "MHr" },
	{ value: ExternalSource.Paycom, label: "Paycom", mark: "Pcm" },
	{ value: ExternalSource.PaycomSftp, label: "Paycom SFTP", mark: "SFT" },
	{ value: ExternalSource.Paycor, label: "Paycor", mark: "Pcr" },
	{ value: ExternalSource.Sbv, label: "SBV", mark: "SBV" },
	{ value: ExternalSource.TimePro, label: "TimePro", mark: "TP" },
	{ value: ExternalSource.UkgReady, label: "UKG Ready", mark: "UKG" },
	{ value: ExternalSource.Webclock, label: "WebClock", mark: "WC" },
	{ value: ExternalSource.Workday, label: "Workday", mark: "WD" },
	{ value: ExternalSource.Demo, label: "Demo", mark: "Dm" },
];

const HRIS_SOURCE_BY_VALUE: ReadonlyMap<ExternalSource, HrisSourceOption> = new Map(HRIS_SOURCES.map((option) => [option.value, option]));

/**
 * The vendor's row in this catalog, or `undefined` for a source outside it (`clipboard`, or a value
 * the backend added before the frontend caught up). Callers decide what an unknown vendor looks
 * like rather than being handed a fabricated one.
 */
export const hrisSourceOption = (source: ExternalSource): HrisSourceOption | undefined => HRIS_SOURCE_BY_VALUE.get(source);

/**
 * `BrandMarkTile` colours for a vendor: a hue from the app-wide categorical ramp, taken by the
 * vendor's index in this catalog — the same index-based scheme the job groups use, so a vendor
 * keeps one colour across the picker and its configured card.
 *
 * Purely a scanning aid; the letterform and the label carry the identity. The ramp is shorter than
 * the catalog, so later vendors reuse an early hue — eight rows apart, never adjacent.
 * `undefined` for a source outside the catalog, which leaves the tile on the brand default.
 */
export const hrisSourceTileColors = (source: ExternalSource): { color: string; tint: string } | undefined => {
	const index = HRIS_SOURCES.findIndex((option) => option.value === source);
	if (index < 0) return undefined;
	const hue = categoricalColorAt(index).dot;
	return { color: hue, tint: alpha(hue, ALPHA.BRAND_TINT) };
};

export const HRIS_TYPES: { value: HrisIntegrationType; label: string }[] = [
	{ value: "employees", label: "Employees" },
	{ value: "shifts", label: "Shifts" },
	{ value: "time_off", label: "Time Off" },
];

export const EMR_TYPES: { value: EmrType; label: string }[] = [
	{ value: EmrType.PCC, label: "PCC" },
	{ value: EmrType.Yardi, label: "Yardi" },
	{ value: EmrType.Matrix, label: "Matrix" },
	{ value: EmrType.Matrix360, label: "Matrix 360" },
	{ value: EmrType.ReliableHealth, label: "Reliable Health" },
];

/** Labels for the facility-id and org-id fields per EMR type. Note Yardi's facility is a UUID and its
 *  org field is the Tenant ID (its identifiers are "inverted" vs the other vendors). */
export const EMR_FIELD_LABELS: Record<EmrType, { facilityId: string; orgId?: string }> = {
	[EmrType.PCC]: { facilityId: "Facility ID", orgId: "Org UUID" },
	[EmrType.Yardi]: { facilityId: "Facility UUID", orgId: "Tenant ID" },
	[EmrType.Matrix]: { facilityId: "Facility ID" },
	[EmrType.Matrix360]: { facilityId: "Facility ID", orgId: "Facility OID" },
	[EmrType.ReliableHealth]: { facilityId: "Facility ID" },
};

/** Which field holds a UUID (for format validation) per EMR type — PCC's org UUID vs Yardi's facility
 *  UUID sit in different columns. Matrix 360's orgId is a Facility OID (validated separately), not here. */
export const EMR_UUID_FIELD: Partial<Record<EmrType, "facilityId" | "orgId">> = {
	[EmrType.PCC]: "orgId",
	[EmrType.Yardi]: "facilityId",
};
